<script>
  import { External, M } from '../external';
  import { onMount } from 'svelte';
  import CommentAdminService from './service';
  import CommentWithResource from './CommentWithResource.svelte';

  export let onComponentReady;
  export let resourceType = null; // Restrict to specific resource type if provided
  export let resourceId = null; // Restrict to specific resourceId if provided
  export let resourceDefinitions = [];

  let resourceData = {};
  let visibleResourceDefinitions = [];
  let comments = {
    limit: 10,
    data: {
      comments: [],
      hasNext: false,
      nextTimestamp: null
    },
    isLoading: false,
    reset: () => {
      comments.data = {
        comments: [],
        hasNext: false,
        nextTimestamp: null
      };
    },
    fetch: async () => {
      comments.isLoading = true;
      const response = await fetchCommentsBasedOnCurrentState();

      response.comments = response.comments || [];
      comments.data.hasNext = response.hasNext;
      comments.data.nextTimestamp = response.nextTimestamp;
      comments.data.comments = [...comments.data.comments, ...response.comments];
      comments.isLoading = false;
    },
    onShowMoreComments: () => comments.fetch()
  };

  const tabSection = {
    currentCategory: 'all',
    currentFilter: resourceId || 'all',
    change: (selected) => {
      if (tabSection.currentCategory === selected) {
        return;
      }
      tabSection.currentCategory = selected;
      tabSection.currentFilter = resourceId || 'all';
      comments.reset();
      comments.fetch();
    },
    onFilterChanged: () => {
      comments.reset();
      comments.fetch();
    },
    categories: {
      all: {
        title: M.ALL,
        isVisible: () => true,
        filters: []
      },
      waiting_approval: {
        title: M.WAITING_APPROVAL,
        isVisible: () => true,
        filters: []
      }
    }
  };

  async function fetchCommentsBasedOnCurrentState() {
    const { currentCategory, currentFilter } = tabSection;
    const { nextTimestamp } = comments.data;
    const { limit } = comments;
    const order = 'desc';

    if (currentCategory === 'all') {
      if (resourceType) {
        return resourceId
          ? await CommentAdminService.getAllRootCommentsByResource(
              resourceType,
              resourceId,
              nextTimestamp,
              limit,
              order
            )
          : await CommentAdminService.getAllRootCommentsByOnlyResourceType(resourceType, nextTimestamp, limit, order);
      }

      return await CommentAdminService.getAllComments(nextTimestamp, limit, order);
    }

    if (currentCategory === 'waiting_approval') {
      if (resourceId) {
        return await CommentAdminService.getUnapprovedCommentsBySpecificResource(
          resourceType,
          resourceId,
          nextTimestamp,
          limit,
          order
        );
      }

      if (currentFilter === 'all') {
        return resourceType
          ? await CommentAdminService.getUnapprovedCommentsByResource(resourceType, nextTimestamp, limit, order)
          : await CommentAdminService.getUnapprovedComments(nextTimestamp, limit, order);
      } else {
        return resourceType
          ? await CommentAdminService.getUnapprovedCommentsBySpecificResource(
              resourceType,
              currentFilter,
              nextTimestamp,
              limit,
              order
            )
          : await CommentAdminService.getUnapprovedCommentsByResource(currentFilter, nextTimestamp, limit, order);
      }
    }

    return currentFilter === 'all'
      ? await CommentAdminService.getAllRootCommentsByOnlyResourceType(currentCategory, nextTimestamp, limit, order)
      : await CommentAdminService.getAllRootCommentsByResource(
          currentCategory,
          currentFilter,
          nextTimestamp,
          limit,
          order
        );
  }

  function initializeCategories() {
    // filter by resourceType if exists. best to filter since we won't use rest of them. `fetchResourceData` will go over this and fetch additional metadata.
    visibleResourceDefinitions = resourceDefinitions.filter((def) => !resourceType || def.id === resourceType);

    // If no resourceType is specified, add all resource definitions as filters
    if (!resourceType) {
      // Add each resource definition to categories
      visibleResourceDefinitions.forEach((def) => {
        tabSection.categories[def.id] = {
          title: def.title,
          isVisible: () => true,
          filters: [],
          allowDelete: def.allowDelete
        };
      });

      tabSection.categories.waiting_approval.filters = visibleResourceDefinitions.map((def) => ({
        id: def.id,
        name: def.title
      }));
    }
  }

  async function fetchResourceData() {
    // Fetch data for all visible resource definitions
    const results = await Promise.all(
      visibleResourceDefinitions.map(async (def) => {
        try {
          const result = await def.fetchResourceData();
          return {
            id: def.id,
            options: result.options || [],
            data: result.data || []
          };
        } catch (error) {
          console.error(`Error fetching items for ${def.id}:`, error);
          return { id: def.id, options: [], data: [] };
        }
      })
    );

    // Update the filters for each category and store the data
    results.forEach((result) => {
      if (tabSection.categories[result.id]) {
        tabSection.categories[result.id].filters = result.options;
      }
      resourceData[result.id] = result.data;

      // If only resourceType is defined and it matches this result,
      // use its options for waiting_approval filters
      if (resourceType && resourceType === result.id && !resourceId) {
        tabSection.categories.waiting_approval.filters = result.options;
      }
    });
  }

  const onDeleteComment = async (_comment) => {
    const isDeleted = await CommentAdminService.deleteComment(_comment.id);
    if (isDeleted) {
      comments.data.comments = comments.data.comments.filter((reply) => reply.id !== _comment.id);
    }
  };

  const onDeleteContext = () => {
    External.Tools.prompt(M.DELETE, M.DELETE_COMMENT_CONTEXT_WARNING, () => {
      const resourceType = tabSection.currentCategory;
      const resourceId = tabSection.currentFilter;

      CommentAdminService.deleteContext(resourceType, resourceId);
      tabSection.categories[resourceType].filters = tabSection.categories[resourceType].filters.filter(
        (context) => context.id !== resourceId
      );

      tabSection.currentFilter = 'all';
      tabSection.onFilterChanged();
    });
  };

  onMount(async () => {
    // Initialize categories from resource definitions
    initializeCategories();

    // Make sure WebPlatform.hash is ready.
    await new Promise((resolve) => External.WebPlatform.getSessionDetails(resolve));

    await fetchResourceData();

    // Initialize with resource constraints if provided
    if (resourceType && tabSection.categories[resourceType]) {
      tabSection.currentCategory = resourceType;
      if (resourceId) {
        tabSection.currentFilter = resourceId;
      }
    }

    await comments.fetch();
    onComponentReady();
  });

  const quillCss = `${External.resources}/styles/quill/2.0.3/quill.snow.css?v=${External.resourcesVersion}`;
  const quillMentionCss = `${External.resources}/styles/quill-mention/6.0.2/quill.mention.css?v=${External.resourcesVersion}`;
</script>

<svelte:head>
  <link rel="stylesheet" type="text/css" href={quillCss} />
  <link rel="stylesheet" type="text/css" href={quillMentionCss} />
</svelte:head>

<ul class="upper-navigation upper-navigation-full f-left col20">
  {#each Object.entries(tabSection.categories) as [key, tab]}
    {#if tab.isVisible()}
      <li>
        <a
          class={key === tabSection.currentCategory ? 'active' : ''}
          data-tab={key}
          on:click={() => tabSection.change(key)}>
          {key === resourceType && key !== 'all' && key !== 'waiting_approval' ? M.ALL : tab.title}
        </a>
      </li>
    {/if}
  {/each}
</ul>

<div class="col20 f-left mt-5">
  <div class="col20 f-left flex flex-end align-center">
    {#if tabSection.categories[tabSection.currentCategory]?.allowDelete && tabSection.currentFilter !== 'all'}
      <a class="middle-button blue f-left mr-5" on:click={onDeleteContext}>{M.DELETE_COMMENT_CONTEXT}</a>
    {/if}
    {#if !resourceId && tabSection.categories[tabSection.currentCategory]?.filters?.length > 0}
      <select
        bind:value={tabSection.currentFilter}
        on:change={tabSection.onFilterChanged}
        class="f-right"
        style="max-width: 200px;">
        <option value="all">{M.ALL}</option>
        {#each tabSection.categories[tabSection.currentCategory].filters as filter (filter.id)}
          <option value={filter.id}>{filter.name}</option>
        {/each}
      </select>
    {/if}
  </div>
  {#if comments.isLoading}
    <div class="mt-25 small-centered-loading-icon" />
  {/if}
  {#if !comments.isLoading && comments.data.comments.length === 0}
    <div class="no-data-box">
      <h3 class="text-center">{M.NO_MESSAGES}</h3>
    </div>
  {/if}
</div>

{#each comments.data.comments as comment (comment.id + tabSection.currentCategory)}
  <CommentWithResource {comment} limit={comments.limit} {onDeleteComment} {resourceData} {resourceDefinitions} />
{/each}

{#if comments.data.hasNext}
  <div class="col20 f-left mb-15 t-center post-more-comments-wrapper">
    <a on:click={comments.onShowMoreComments} class="comments-show-all circle-effect">{M.SHOW_MORE_COMMENTS}</a>
  </div>
{/if}
